import React, { useEffect, useMemo, useState } from "react";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { FiEye } from "react-icons/fi";
import { getApi } from "../../utils/api";
import {
  API_LEAVE_GET_HR_OOD_V2,
  GET_ALL_EMPLOYEES_NAME,
} from "../../config/Endpoints";
import { displayFormatDate } from "../../utils/helpers";
import { ThreeDots } from "react-loader-spinner";
import ApproveOODModal from "./Modals/ApproveOODModal";
import PageShell from "../../v2/components/shared/page-shell/page-shell.tsx";
import { EmptyStateMessage } from "../../v2/components/shared/empty-state-message/empty-state-message.tsx";
import {
  Button,
  ResponsiveDataTable,
  Select,
} from "../../v2/components/ui/index.ts";
import { payrollOptions } from "../../constants/payrollOptions";
import styles from "./leave.module.css";

function getRmOrPmApproval(approvals) {
  if (!Array.isArray(approvals)) return null;
  return (
    approvals.find((a) => a.approverType === "RM") ||
    approvals.find((a) => a.approverType === "PM") ||
    null
  );
}

const ApproveOnOfficialDutyHR = ({ title, description }) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [timesheet, setTimesheet] = useState([]);
  const accessToken = Cookies.get("accessToken");
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [employeeIdFilter, setEmployeeIdFilter] = useState("");
  const [payrollFilter, setPayrollFilter] = useState("");
  const [appliedEmployeeId, setAppliedEmployeeId] = useState("");
  const [appliedPayroll, setAppliedPayroll] = useState("");

  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [limit, setLimit] = useState(10);

  const fetchEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const res = await getApi(GET_ALL_EMPLOYEES_NAME, accessToken);
      const root = res.data;
      const users = Array.isArray(root)
        ? root
        : Array.isArray(root?.data)
          ? root.data
          : [];
      setEmployeeOptions(
        users.map((user) => ({
          value: String(user.id),
          label: user.empName || `Employee ${user.id}`,
        })),
      );
    } catch (err) {
      setEmployeeOptions([]);
      toast.error(
        err?.response?.data?.message || "Unable to load employee names",
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
    } finally {
      setEmployeesLoading(false);
    }
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(currentPage - 1),
        size: String(limit),
      });
      if (appliedEmployeeId) params.set("employeeId", appliedEmployeeId);
      if (appliedPayroll) params.set("payroll", appliedPayroll);

      const res = await getApi(
        `${API_LEAVE_GET_HR_OOD_V2}?${params.toString()}`,
        accessToken,
      );
      setData(res?.data?.content || []);
      setTotalElements(res?.data?.totalElements ?? 0);
    } catch (err) {
      toast.error(
        err?.response?.data?.message ||
        "Something went wrong please try again later",
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );
      setData([]);
      setTotalElements(0);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  useEffect(() => {
    fetchData();
  }, [currentPage, limit, appliedEmployeeId, appliedPayroll]);

  const handleFilter = () => {
    setAppliedEmployeeId(employeeIdFilter);
    setAppliedPayroll(payrollFilter);
    setCurrentPage(1);
  };

  const resetToDefaultIfFiltersCleared = (nextEmployeeId, nextPayroll) => {
    if (nextEmployeeId || nextPayroll) return;
    setAppliedEmployeeId("");
    setAppliedPayroll("");
    setCurrentPage(1);
  };

  const handleEmployeeFilterChange = (value) => {
    setEmployeeIdFilter(value);
    resetToDefaultIfFiltersCleared(value, payrollFilter);
  };

  const handlePayrollFilterChange = (value) => {
    setPayrollFilter(value);
    resetToDefaultIfFiltersCleared(employeeIdFilter, value);
  };

  const tableData = useMemo(() => {
    if (!data?.length) return [];
    return data.map((item) => {
      const appr = getRmOrPmApproval(item.approvals);
      const rawComment = appr?.comment;
      const dlComment =
        rawComment != null && String(rawComment).trim() !== ""
          ? rawComment
          : "—";

      const reportingManagerName =
        item.reportingManagerName != null &&
        String(item.reportingManagerName).trim() !== ""
          ? item.reportingManagerName
          : "—";

      return {
        employeeName:
          item.employeeName != null &&
          String(item.employeeName).trim() !== ""
            ? item.employeeName
            : "—",
        leaveDate: item.date,
        reportingManagerName,
        dlComment,
        _original: item,
      };
    });
  }, [data]);

  const columns = useMemo(
    () => [
      {
        id: "employeeName",
        header: "Employee Name",
        accessor: "employeeName",
        sortable: true,
        truncate: true,
      },
      {
        id: "leaveDate",
        header: "Date",
        accessor: "leaveDate",
        sortable: true,
        getSortValue: (row) =>
          row._original?.date ? new Date(row._original.date).getTime() : 0,
        render: (row) => displayFormatDate(row.leaveDate),
        getTitleText: (row) =>
          row.leaveDate != null ? String(row.leaveDate) : "",
      },
      {
        id: "reportingManagerName",
        header: "Reporting Manager",
        accessor: "reportingManagerName",
        sortable: true,
        truncate: true,
      },

      {
        id: "action",
        header: "Action",
        sortable: false,
        nowrap: true,
        render: (row) => (
          <button
            type="button"
            className="action-btn action-btn--view"
            onClick={() => {
              setTimesheet(row._original.entries || []);
              setSelectedEntry(row._original);
              setModalOpen(true);
            }}
          >
            <FiEye size={14} aria-hidden />
            View
          </button>
        ),
      },
    ],
    [],
  );

  const tableRows = useMemo(
    () => tableData.map((row) => ({ ...row })),
    [tableData],
  );

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedEntry(null);
    setTimesheet([]);
    fetchData();
  };

  return (
    <PageShell>
      <div className={styles.oodHrFilters}>
        <Select
          id="ood-hr-employee-filter"
          label="Employee Name"
          placeholder={employeesLoading ? "Loading..." : "Select Employee Name"}
          searchable
          isClearable
          value={employeeIdFilter}
          onChange={handleEmployeeFilterChange}
          options={employeeOptions}
          disabled={employeesLoading}
        />
        <Select
          id="ood-hr-payroll-filter"
          label="Employee Type"
          placeholder="Select Employee Type"
          isClearable
          value={payrollFilter}
          onChange={handlePayrollFilterChange}
          options={payrollOptions}
        />
        <Button
          className={styles.oodHrFilterButton}
          onClick={handleFilter}
          disabled={isLoading}
        >
          Filter
        </Button>
      </div>

      {isLoading ? (
        <div className="d-flex justify-content-center py-5">
          <ThreeDots
            height="50"
            width="80"
            radius="9"
            color="var(--color-accent-500, #2563eb)"
            ariaLabel="three-dots-loading"
            visible
          />
        </div>
      ) : tableData.length === 0 ? (
        <EmptyStateMessage message="No Record Available" />
      ) : (
        <ResponsiveDataTable
          aria-label="HR On Official Duty — fully approved timesheets"
          columns={columns}
          data={tableRows}
          defaultSortKey="leaveDate"
          defaultSortDir="desc"
          pagination
          page={currentPage}
          pageSize={limit}
          totalItems={totalElements}
          onPageChange={setCurrentPage}
          onPageSizeChange={(n) => {
            setLimit(n);
            setCurrentPage(1);
          }}
          pageSizeOptions={[5, 10, 20, 30, 40]}
          totalLabel={
            <>
              Total Requests: <strong>{totalElements}</strong>
            </>
          }
        />
      )}


      <ApproveOODModal
        type="HR"
        open={modalOpen}
        timesheet={timesheet}
        selectedEntry={selectedEntry}
        onClose={handleModalClose}
      />
    </PageShell>
  );
};

export default ApproveOnOfficialDutyHR;
